import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTornaIndietro } from '../common/navigazione';
import { getOrdine, getClienti, getCorrieri, getDatiAzienda, createFatturaElettronica, exportOrdinePdf, creaSpedizionePaccofacile } from '../services/api';
import { Container, Card, Button, Row, Col, Spinner, Alert, Table, Tab, Nav, Badge } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import PreviewFatturaModal from '../components/modals/PreviewFatturaModal'; // Importa il modale
import OrderMessages from '../components/OrderMessages';
import RimborsoOrdineBox from '../common/RimborsoOrdineBox';

const MESSAGGI_BADGE_BG = {
  'Risposto': 'success',
  'Da Rispondere': 'danger',
  'Non Presente': 'secondary',
};

function OrdiniDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = location.state?.from || '/ordini';
  const tornaIndietro = useTornaIndietro('/ordini');
  const [ordine, setOrdine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPrinting, setIsPrinting] = useState(false);
  const [error, setError] = useState(null);
  const [allClienti, setAllClienti] = useState([]);
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false); // Stato per il modale
  const [activeTab, setActiveTab] = useState('dettagli'); // Stato per gestire il tab attivo
  const [creandoSpedizione, setCreandoSpedizione] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 767 });

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const [ordineRes, clientiRes, , datiAziendaRes] = await Promise.all([
          getOrdine(id),
          getClienti(0, 100000),
          getCorrieri(0, 100000),
          getDatiAzienda()
        ]);
        
        setOrdine(ordineRes.data);
        setAllClienti(clientiRes.data.clienti);

      } catch (err) {
        setError('Errore nel caricamento dei dettagli dell\'ordine.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [id]);

  const handlePrint = async () => {
    setIsPrinting(true);
    try {
        const response = await exportOrdinePdf(id);
        const file = new Blob([response.data], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(file);
        
        // Crea nome file formattato: Anno - Ordine Nr - Nome Cliente
        const anno = new Date(ordine.data_ordine).getFullYear();
        const nomeCliente = ordine.cliente.cognome 
            ? `${ordine.cliente.nome} ${ordine.cliente.cognome}`
            : ordine.cliente.denominazione_sociale || ordine.cliente.nome;
        const fileName = `${anno} - Ordine ${ordine.id} - ${nomeCliente}.pdf`;
        
        // Crea link temporaneo per download con nome personalizzato
        const link = document.createElement('a');
        link.href = fileURL;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Pulisci l'URL dopo il download
        setTimeout(() => URL.revokeObjectURL(fileURL), 100);
    } catch (error) {
        console.error('Errore durante la generazione del PDF dell\'ordine', error);
        alert('Impossibile generare il PDF dell\'ordine.');
    } finally {
        setIsPrinting(false);
    }
  };

  const handleShowPreview = () => setShowPreviewModal(true);
  const handleClosePreview = () => setShowPreviewModal(false);

  const handleCreaSpedizione = async () => {
    if (!window.confirm(
      'Creare la spedizione su PaccoFacile?\n\n' +
      'Resta da acquistare: la troverai in "Spedizioni configurate" sul sito PaccoFacile, ' +
      'dove puoi cambiare il servizio e pagarla.'
    )) return;

    setCreandoSpedizione(true);
    try {
      const { data } = await creaSpedizionePaccofacile(ordine.id);
      alert(`Spedizione creata: ${data.corriere} ${data.servizio} - € ${data.prezzo}`);
      const aggiornato = await getOrdine(id);
      setOrdine(aggiornato.data);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || 'Errore durante la creazione della spedizione.');
    } finally {
      setCreandoSpedizione(false);
    }
  };

  const handleCopyLinkValutazione = async () => {
    const link = `https://www.modenacinema.it/account/ordini?valuta=${ordine.id}`;
    const nome = ordine.cliente?.nome || '';
    const messaggio = `Ciao ${nome}! 👋 Grazie per il tuo ordine su Modena Cinema.\nSe hai un attimo, lasciaci una valutazione del servizio: bastano 30 secondi e ricevi anche punti fedeltà!\n${link}`;
    try {
      await navigator.clipboard.writeText(messaggio);
      alert('Messaggio con link copiato negli appunti. Incollalo su WhatsApp o dove preferisci.');
    } catch (err) {
      console.error(err);
      alert(`Impossibile copiare automaticamente. Link: ${link}`);
    }
  };

  const handleConfirmCreateFattura = async () => {
    setIsCreatingInvoice(true);
    setError(null);
    try {
      const response = await createFatturaElettronica(id);
      setOrdine(prevOrdine => ({
        ...prevOrdine,
        fatture: [...(prevOrdine.fatture || []), response.data]
      }));
      alert("Fattura creata con successo!");
      handleClosePreview();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Errore durante la creazione della fattura.';
      setError(errorMsg);
      alert(`Errore: ${errorMsg}`);
      console.error(err);
    } finally {
      setIsCreatingInvoice(false);
    }
  };

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" />
        <p>Caricamento dettagli ordine...</p>
      </Container>
    );
  }

  if (error && !isCreatingInvoice) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
        <Button variant="primary" onClick={() => tornaIndietro()}>Torna alla lista ordini</Button>
      </Container>
    );
  }

  if (!ordine) {
    return (
      <Container className="mt-5">
        <Alert variant="warning">Ordine non trovato.</Alert>
        <Button variant="primary" onClick={() => tornaIndietro()}>Torna alla lista ordini</Button>
      </Container>
    );
  }

  return (
    <>
      <Container className="mt-4">
        {error && isCreatingInvoice && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
        
        <Row className="d-print-none mb-3 align-items-center">
          <Col>
            <h2 className={isMobile ? "mb-1 h4" : "mb-0"}>📋 Ordine #{ordine.id}</h2>
            <Badge bg={
              ordine.stato === 'Concluso' ? 'success' :
              ordine.stato === 'Da Evadere' ? 'warning' :
              ordine.stato === 'Confermato' ? 'info' :
              ordine.stato === 'Parz.Concluso' ? 'primary' :
              ordine.stato === 'Annullato' ? 'danger' :
              ordine.stato === 'Rimborsato' ? 'dark' :
              ordine.stato === 'Non Ritirato - Contrassegno' ? 'danger' :
              'secondary'
            } className="mt-1">
              {ordine.stato}
            </Badge>
          </Col>
          {!isMobile && (
            <Col xs="auto" className="ms-auto">
              {(!ordine.fatture || ordine.fatture.length === 0) && ordine.stato === 'Concluso' && (
                <Button 
                  variant="success" 
                  onClick={handleShowPreview} 
                  disabled={isCreatingInvoice}
                  className="me-2"
                >
                  {isCreatingInvoice ? <Spinner as="span" animation="border" size="sm" /> : '🧾 Crea Fattura'}
                </Button>
              )}
              {ordine.stato === 'Concluso' && (
                <Button variant="outline-primary" onClick={handleCopyLinkValutazione} className="me-2">
                  ⭐ Copia Link Valutazione
                </Button>
              )}
              <Button variant="secondary" onClick={() => tornaIndietro()} className="me-2">
                ⬅️ {backTo.includes('/messaggi') ? 'Torna ai Messaggi' : 'Torna alla Lista'}
              </Button>
              <Button variant="warning" onClick={() => navigate(`/ordini/edit/${id}`)} className="me-2">
                ✏️ Modifica
              </Button>
              <Button variant="info" onClick={handlePrint} disabled={isPrinting}>
                {isPrinting ? <Spinner as="span" animation="border" size="sm" /> : '🖨️ Stampa'}
              </Button>
            </Col>
          )}
        </Row>
        
        <RimborsoOrdineBox
          ordine={ordine}
          onChanged={async () => {
            try {
              const res = await getOrdine(id);
              setOrdine(res.data);
            } catch (err) {
              console.error(err);
            }
          }}
        />

        {/* Mobile Action Buttons */}
        {isMobile && (
          <Row className="mb-3 g-2">
            <Col xs={6}>
              <Button 
                variant="secondary" 
                onClick={() => tornaIndietro()} 
                className="w-100"
                size="sm"
              >
                ⬅️ {backTo.includes('/messaggi') ? 'Messaggi' : 'Lista'}
              </Button>
            </Col>
            <Col xs={6}>
              <Button 
                variant="warning" 
                onClick={() => navigate(`/ordini/edit/${id}`)} 
                className="w-100"
                size="sm"
              >
                ✏️ Modifica
              </Button>
            </Col>
            {(!ordine.fatture || ordine.fatture.length === 0) && ordine.stato === 'Concluso' && (
              <Col xs={6}>
                <Button 
                  variant="success" 
                  onClick={handleShowPreview} 
                  disabled={isCreatingInvoice}
                  className="w-100"
                  size="sm"
                >
                  {isCreatingInvoice ? <Spinner as="span" animation="border" size="sm" /> : '🧾 Fattura'}
                </Button>
              </Col>
            )}
            {ordine.stato === 'Concluso' && (
              <Col xs={6}>
                <Button
                  variant="outline-primary"
                  onClick={handleCopyLinkValutazione}
                  className="w-100"
                  size="sm"
                >
                  ⭐ Link Valutazione
                </Button>
              </Col>
            )}
            <Col xs={6}>
              <Button 
                variant="info" 
                onClick={handlePrint} 
                disabled={isPrinting}
                className="w-100"
                size="sm"
              >
                {isPrinting ? <Spinner as="span" animation="border" size="sm" /> : '🖨️ Stampa'}
              </Button>
            </Col>
          </Row>
        )}

        <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
          <Card>
            <Card.Header className={isMobile ? "bg-light p-2" : "bg-light"}>
              <Nav variant="tabs" className={isMobile ? "card-header-tabs flex-column" : "card-header-tabs"}>
                <Nav.Item>
                  <Nav.Link eventKey="dettagli" className={isMobile ? "py-2 px-3" : "d-flex align-items-center gap-2"}>
                    {isMobile ? "📋 Dettagli" : <><span>📋</span> <span>Dettagli Ordine</span></>}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="prodotti" className={isMobile ? "py-2 px-3" : "d-flex align-items-center gap-2"}>
                    {isMobile ? (
                      <>📦 Prodotti {ordine.dettagli && ordine.dettagli.length > 0 && 
                        <span className="badge bg-primary rounded-pill ms-1">{ordine.dettagli.length}</span>}
                      </>
                    ) : (
                      <>
                        📦 <span>Prodotti</span>
                        {ordine.dettagli && ordine.dettagli.length > 0 && 
                          <span className="badge bg-primary rounded-pill">{ordine.dettagli.length}</span>
                        }
                      </>
                    )}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="finanziario" className={isMobile ? "py-2 px-3" : "d-flex align-items-center gap-2"}>
                    {isMobile ? (
                      <>💰 Finanziario <span className="badge bg-success rounded-pill ms-1">€ {(ordine.totale_ordine || 0).toFixed(2)}</span></>
                    ) : (
                      <>
                        💰 <span>Riepilogo Finanziario</span>
                        <span className="badge bg-success rounded-pill">€ {(ordine.totale_ordine || 0).toFixed(2)}</span>
                      </>
                    )}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="spedizione" className={isMobile ? "py-2 px-3" : "d-flex align-items-center gap-2"}>
                    {isMobile ? (
                      <>🚚 Spedizione {ordine.tracking_code && <span className="badge bg-info rounded-pill ms-1">✓</span>}</>
                    ) : (
                      <>
                        🚚 <span>Spedizione</span>
                        {ordine.tracking_code && 
                          <span className="badge bg-info rounded-pill">Tracciato</span>
                        }
                      </>
                    )}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="messaggi" className={isMobile ? "py-2 px-3" : "d-flex align-items-center gap-2"}>
                    {isMobile ? (
                      <>💬 Messaggi <span className={`badge bg-${MESSAGGI_BADGE_BG[ordine.stato_messaggi] || 'secondary'} rounded-pill ms-1`}>{ordine.stato_messaggi}</span></>
                    ) : (
                      <>
                        💬 <span>Messaggi IBS</span>
                        <span className={`badge bg-${MESSAGGI_BADGE_BG[ordine.stato_messaggi] || 'secondary'} rounded-pill`}>{ordine.stato_messaggi}</span>
                      </>
                    )}
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Card.Header>
            <Card.Body className="p-4">
              <Tab.Content>
                <Tab.Pane eventKey="dettagli" className="mt-3">
                  <h5 className="text-primary mb-4">📋 Informazioni Generali Ordine</h5>
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <strong className="text-muted">ID Ordine:</strong>
                        <p className="mb-0 fs-5">#{ordine.id}</p>
                      </div>
                      <div className="mb-3">
                        <strong className="text-muted">Data Ordine:</strong>
                        <p className="mb-0">{new Date(ordine.data_ordine).toLocaleDateString('it-IT')}</p>
                      </div>
                      <div className="mb-3">
                        <strong className="text-muted">Stato Ordine:</strong>
                        <div className="mt-1">
                          <Badge bg={
                            ordine.stato === 'Concluso' ? 'success' :
                            ordine.stato === 'Da Evadere' ? 'warning' :
                            ordine.stato === 'Confermato' ? 'info' :
                            ordine.stato === 'Parz.Concluso' ? 'primary' :
                            ordine.stato === 'Annullato' ? 'danger' :
                            ordine.stato === 'Rimborsato' ? 'dark' :
              ordine.stato === 'Non Ritirato - Contrassegno' ? 'danger' :
                            'secondary'
                          } className="fs-6">
                            {ordine.stato}
                          </Badge>
                        </div>
                      </div>
                      <div className="mb-3">
                        <strong className="text-muted">Origine:</strong>
                        <div className="mt-1">
                          <Badge bg="secondary" className="fs-6">
                            {ordine.origine === 'SITO WEB' && '🌐'} 
                            {ordine.origine === 'NEGOZIO' && '🏪'} 
                            {ordine.origine === 'IBS' && '📚'} 
                            {ordine.origine === 'FELTRINELLI' && '📖'} 
                            {' '}{ordine.origine}
                          </Badge>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <strong className="text-muted">Cliente:</strong>
                        <p className="mb-0">
                          <Link to={`/clienti/${ordine.cliente.id}`} className="text-decoration-none">
                            👤 {ordine.cliente.nome} {ordine.cliente.cognome || ordine.cliente.denominazione_sociale}
                          </Link>
                        </p>
                      </div>
                      <div className="mb-3">
                        <strong className="text-muted">Corriere:</strong>
                        <p className="mb-0">
                          {ordine.corriere ? (
                            <Link to={`/corrieri/${ordine.corriere.id}`} className="text-decoration-none">
                              🚚 {ordine.corriere.denominazione}
                            </Link>
                          ) : (
                            <span className="text-muted">Da assegnare</span>
                          )}
                        </p>
                      </div>
                      <div className="mb-3">
                        <strong className="text-muted">Metodo Pagamento:</strong>
                        <p className="mb-0">
                          {ordine.payment_method ? (
                            <Badge bg="info">{ordine.payment_method}</Badge>
                          ) : (
                            <span className="text-muted">Non specificato</span>
                          )}
                        </p>
                      </div>
                      {ordine.numero_ordine_marketplace && (
                        <div className="mb-3">
                          <strong className="text-muted">Ordine Marketplace:</strong>
                          <p className="mb-0 font-monospace">{ordine.numero_ordine_marketplace}</p>
                        </div>
                      )}
                    </Col>
                  </Row>
                </Tab.Pane>

                <Tab.Pane eventKey="prodotti" className="mt-3">
                  <h5 className="text-success mb-4">📦 Prodotti nell'Ordine</h5>
                  
                  {isMobile ? (
                    // Mobile: Card layout
                    <div className="mobile-products-list">
                      {ordine.dettagli.map(d => {
                        const item = d.film || d.musica || d.servizio || (d.collectible && { ...d.collectible, titolo: d.collectible.nome }) || { descrizione_prodotto_libero: d.descrizione_prodotto_libero, prezzo_unitario: d.prezzo_unitario_libero };
                        const prezzoUnitario = (d.prodotto_libero ? d.prezzo_unitario_libero : d.prezzo_unitario) ?? d.prezzo_unitario ?? d.prezzo_unitario_libero ?? 0;
                        const ivaRiga = (d.prodotto_libero ? d.iva_percentuale_libero : d.iva_percentuale) ?? d.iva_percentuale ?? 0;
                        const descrizione = item.titolo || (item.artista ? `${item.artista} - ${item.titolo}`: item.descrizione_prodotto_libero);
                        const totaleRiga = d.quantita * prezzoUnitario;
                        
                        // Determina supporto
                        let supporto = '';
                        if (d.film && d.film.supporto) {
                          supporto = d.film.supporto;
                        } else if (d.musica && d.musica.supporto) {
                          supporto = d.musica.supporto;
                        } else if (d.servizio) {
                          supporto = "Servizio";
                        } else if (d.prodotto_libero) {
                          supporto = "Prodotto Libero";
                        }

                        const ean = item.barcode_ibs || '';
                        const ubicazione = d.ubicazione_scelta || item.ubicazione || '';
                        const linkScheda = d.film_id ? `/film/${d.film_id}` : d.musica_id ? `/musica/${d.musica_id}` : null;

                        return (
                          <Card key={d.id} className="mb-3 shadow-sm">
                            <Card.Body className="p-3">
                              <div className="mb-2">
                                {linkScheda ? (
                                  <Link to={linkScheda} className="text-decoration-none d-block mb-1">
                                    <strong>{descrizione}</strong>
                                  </Link>
                                ) : (
                                  <strong className="d-block mb-1">{descrizione}</strong>
                                )}
                                {supporto && <Badge bg="secondary" className="me-2">{supporto}</Badge>}
                                {(item.sku || item.barcode) && (
                                  <small className="text-muted font-monospace">{item.sku || item.barcode}</small>
                                )}
                                {(ean || ubicazione) && (
                                  <div className="small text-muted mt-1">
                                    {ean && <>EAN: {ean}</>}
                                    {ean && ubicazione && ' · '}
                                    {ubicazione && <>Ubicazione: {ubicazione}</>}
                                  </div>
                                )}
                              </div>
                              
                              <div className="border-top pt-2">
                                <Row className="g-2">
                                  <Col xs={4}>
                                    <small className="text-muted d-block">Quantità</small>
                                    <strong className="h5 mb-0">{d.quantita}</strong>
                                  </Col>
                                  <Col xs={4}>
                                    <small className="text-muted d-block">Prezzo Unit.</small>
                                    <strong className="text-primary">€ {prezzoUnitario.toFixed(2)}</strong>
                                  </Col>
                                  <Col xs={4}>
                                    <small className="text-muted d-block">IVA</small>
                                    <strong>{ivaRiga}%</strong>
                                  </Col>
                                </Row>
                              </div>
                              
                              <div className="border-top mt-2 pt-2">
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="text-muted">Totale Riga:</span>
                                  <strong className="h5 mb-0 text-success">€ {totaleRiga.toFixed(2)}</strong>
                                </div>
                              </div>
                            </Card.Body>
                          </Card>
                        );
                      })}
                      
                      <Card className="bg-light">
                        <Card.Body className="p-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <strong className="h6 mb-0">Subtotale:</strong>
                            <strong className="h5 mb-0 text-success">€ {ordine.subtotale_imponibile.toFixed(2)}</strong>
                          </div>
                        </Card.Body>
                      </Card>
                    </div>
                  ) : (
                    // Desktop: Table layout
                    <Table striped bordered hover responsive className="align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Descrizione</th>
                          <th style={{width: '100px'}}>SKU/Barcode</th>
                          <th style={{width: '80px'}}>Q.tà</th>
                          <th style={{width: '120px'}}>Prezzo Unit.</th>
                          <th style={{width: '80px'}}>IVA</th>
                          <th style={{width: '120px'}}>Totale Riga</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ordine.dettagli.map(d => {
                          const item = d.film || d.musica || d.servizio || (d.collectible && { ...d.collectible, titolo: d.collectible.nome }) || { descrizione_prodotto_libero: d.descrizione_prodotto_libero, prezzo_unitario: d.prezzo_unitario_libero };
                        const prezzoUnitario = (d.prodotto_libero ? d.prezzo_unitario_libero : d.prezzo_unitario) ?? d.prezzo_unitario ?? d.prezzo_unitario_libero ?? 0;
                        const ivaRiga = (d.prodotto_libero ? d.iva_percentuale_libero : d.iva_percentuale) ?? d.iva_percentuale ?? 0;
                          const descrizione = item.titolo || (item.artista ? `${item.artista} - ${item.titolo}`: item.descrizione_prodotto_libero);
                          const totaleRiga = d.quantita * prezzoUnitario;

                          // Determina supporto
                          let supporto = '';
                          if (d.film && d.film.supporto) {
                            supporto = d.film.supporto;
                          } else if (d.musica && d.musica.supporto) {
                            supporto = d.musica.supporto;
                          } else if (d.servizio) {
                            supporto = "Servizio";
                          } else if (d.prodotto_libero) {
                            supporto = "Prodotto Libero";
                          }

                          const ean = item.barcode_ibs || '';
                          const ubicazione = d.ubicazione_scelta || item.ubicazione || '';
                          const linkScheda = d.film_id ? `/film/${d.film_id}` : d.musica_id ? `/musica/${d.musica_id}` : null;

                          return (
                            <tr key={d.id}>
                              <td>
                                <div>
                                  {linkScheda ? (
                                    <Link to={linkScheda} className="text-decoration-none">
                                      <strong>{descrizione}</strong>
                                    </Link>
                                  ) : (
                                    <strong>{descrizione}</strong>
                                  )}
                                  {supporto && <><br/><Badge bg="secondary" className="mt-1">{supporto}</Badge></>}
                                  {(ean || ubicazione) && (
                                    <div className="small text-muted mt-1">
                                      {ean && <>EAN: {ean}</>}
                                      {ean && ubicazione && ' · '}
                                      {ubicazione && <>Ubicazione: {ubicazione}</>}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="text-center font-monospace small">{item.sku || item.barcode || '-'}</td>
                              <td className="text-center"><strong>{d.quantita}</strong></td>
                              <td className="text-end">€ {prezzoUnitario.toFixed(2)}</td>
                              <td className="text-center">{ivaRiga}%</td>
                              <td className="text-end"><strong>€ {totaleRiga.toFixed(2)}</strong></td>
                            </tr>
                          )
                        })}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          <td colSpan="5" className="text-end"><strong>Subtotale:</strong></td>
                          <td className="text-end"><strong>€ {ordine.subtotale_imponibile.toFixed(2)}</strong></td>
                        </tr>
                      </tfoot>
                    </Table>
                  )}
                </Tab.Pane>

                <Tab.Pane eventKey="finanziario" className="mt-3">
                  <h5 className="text-warning mb-4">💰 Riepilogo Finanziario</h5>
                  <Row>
                    <Col md={6}>
                      <Card className="bg-light mb-3">
                        <Card.Body>
                          <h6 className="text-muted mb-3">Costi Prodotti</h6>
                          <div className="d-flex justify-content-between mb-2">
                            <span>Costo Prodotti:</span>
                            <strong>€ {(ordine.costo_prodotti || 0).toFixed(2)}</strong>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <span>Costo Marketplace:</span>
                            <strong>€ {(ordine.costo_marketplace || 0).toFixed(2)}</strong>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span>Costo Corriere:</span>
                            <strong>€ {(ordine.costo_reale_corriere || 0).toFixed(2)}</strong>
                          </div>
                        </Card.Body>
                      </Card>
                      
                      <Card className="bg-light">
                        <Card.Body>
                          <h6 className="text-muted mb-3">Marginalità</h6>
                          <div className="d-flex justify-content-between mb-2">
                            <span>Margine Lordo:</span>
                            <strong className={ordine.margine_lordo >= 0 ? 'text-success' : 'text-danger'}>
                              € {(ordine.margine_lordo || 0).toFixed(2)}
                            </strong>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span>Margine netto:</span>
                            <strong className={ordine.margine >= 0 ? 'text-success' : 'text-danger'}>
                              € {(ordine.margine || 0).toFixed(2)} ({(ordine.margine_percentuale || 0).toFixed(1)}%)
                            </strong>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    
                    <Col md={6}>
                      <Card className="border-primary">
                        <Card.Body>
                          <h6 className="text-primary mb-3">Riepilogo Ordine</h6>
                          <div className="d-flex justify-content-between mb-2">
                            <span>Subtotale Imponibile:</span>
                            <strong>€ {ordine.subtotale_imponibile.toFixed(2)}</strong>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <span>Totale IVA:</span>
                            <strong>€ {ordine.totale_iva.toFixed(2)}</strong>
                          </div>
                          {ordine.sconto > 0 && (
                            <div className="d-flex justify-content-between mb-2 text-danger">
                              <span>Sconto:</span>
                              <strong>{ordine.tipo_sconto === 'percentuale' ? `- ${ordine.sconto}%` : `- € ${ordine.sconto.toFixed(2)}`}</strong>
                            </div>
                          )}
                          <div className="d-flex justify-content-between mb-2">
                            <span>Spese Trasporto:</span>
                            <strong>€ {ordine.spese_trasporto.toFixed(2)}</strong>
                          </div>
                          <hr />
                          <div className="d-flex justify-content-between mb-3">
                            <h5 className="mb-0">Totale Ordine:</h5>
                            <h5 className="mb-0 text-primary">€ {ordine.totale_ordine.toFixed(2)}</h5>
                          </div>
                          {ordine.acconto > 0 && (
                            <>
                              <div className="d-flex justify-content-between mb-2 text-success">
                                <span>Acconto Versato:</span>
                                <strong>€ {ordine.acconto.toFixed(2)}</strong>
                              </div>
                              <hr />
                              <div className="d-flex justify-content-between">
                                <h6 className="mb-0">Saldo da Pagare:</h6>
                                <h6 className="mb-0 text-danger">
                                  € {(ordine.totale_ordine - ordine.acconto).toFixed(2)}
                                </h6>
                              </div>
                            </>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Tab.Pane>

                <Tab.Pane eventKey="spedizione" className="mt-3">
                  <h5 className="text-info mb-4">🚚 Informazioni Spedizione</h5>
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <strong className="text-muted">Corriere Selezionato:</strong>
                        <p className="mb-0 fs-5">
                          {ordine.corriere ? (
                            <Link to={`/corrieri/${ordine.corriere.id}`} className="text-decoration-none">
                              {ordine.corriere.denominazione}
                            </Link>
                          ) : (
                            <span className="text-muted">Da assegnare</span>
                          )}
                        </p>
                      </div>
                      <div className="mb-3">
                        <strong className="text-muted">Costo Spedizione:</strong>
                        <p className="mb-0 fs-5">€ {ordine.spese_trasporto.toFixed(2)}</p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <strong className="text-muted">Codice Tracking:</strong>
                        {ordine.tracking_code ? (
                          <p className="mb-0 font-monospace fs-5">
                            <Badge bg="info" className="fs-6">{ordine.tracking_code}</Badge>
                          </p>
                        ) : (
                          <p className="mb-0 text-muted">Non ancora assegnato</p>
                        )}
                      </div>
                      {ordine.tracking_url && (
                        <div className="mb-3">
                          <strong className="text-muted">Traccia Spedizione:</strong>
                          <p className="mb-0">
                            <a href={ordine.tracking_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-info">
                              🔗 Traccia Pacco
                            </a>
                          </p>
                        </div>
                      )}
                      <div className="mb-3">
                        <strong className="text-muted">Spedizione PaccoFacile:</strong>
                        {ordine.paccofacile_shipment_id ? (
                          <p className="mb-0">
                            <Badge bg="secondary" className="fs-6">#{ordine.paccofacile_shipment_id}</Badge>
                            <span className="text-muted ms-2">da acquistare su PaccoFacile</span>
                          </p>
                        ) : (
                          <p className="mb-0">
                            <Button variant="outline-primary" size="sm" onClick={handleCreaSpedizione} disabled={creandoSpedizione}>
                              {creandoSpedizione ? 'Creazione...' : '🚚 Crea spedizione'}
                            </Button>
                          </p>
                        )}
                      </div>
                    </Col>
                  </Row>
                  
                  {ordine.cliente && (
                    <Card className="mt-4 bg-light">
                      <Card.Body>
                        <h6 className="text-muted mb-3">📍 Indirizzo di Spedizione</h6>
                        <p className="mb-1"><strong>{ordine.cliente.nome} {ordine.cliente.cognome || ordine.cliente.denominazione_sociale}</strong></p>
                        {ordine.cliente.indirizzo && <p className="mb-1">{ordine.cliente.indirizzo}</p>}
                        {(ordine.cliente.cap || ordine.cliente.citta || ordine.cliente.provincia) && (
                          <p className="mb-1">
                            {ordine.cliente.cap} {ordine.cliente.citta} ({ordine.cliente.provincia})
                          </p>
                        )}
                        {ordine.cliente.nazione && <p className="mb-0">{ordine.cliente.nazione}</p>}
                      </Card.Body>
                    </Card>
                  )}
                </Tab.Pane>

                <Tab.Pane eventKey="messaggi" className="mt-3">
                  <h5 className="text-primary mb-4">💬 Messaggi IBS</h5>
                  <div style={{ height: '500px' }} className="border rounded">
                    <OrderMessages ordineId={ordine.id} />
                  </div>
                </Tab.Pane>
              </Tab.Content>
            </Card.Body>
          </Card>
        </Tab.Container>
        
      </Container>
      
      <PreviewFatturaModal
        show={showPreviewModal}
        handleClose={handleClosePreview}
        ordineId={id}
        onConfirm={handleConfirmCreateFattura}
      />
    </>
  );
}

export default OrdiniDetail;